import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import type { CvBankDuplicateChoice, CvBankDuplicatePair } from '@nest-recruit/shared';
import { errorMessage } from '../../../lib/api-client';
import { useModule } from '../../../lib/modules';
import { useCanEdit, useCurrentSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { cvBankApi, dropFromLists, dropPair, refreshCvBank } from '../api/cv-bank-api';
import { addedMessage, duplicatesMessage } from '../cv-bank-format';

const UNDO_ADD_REASON = 'Undone straight after adding';

export interface CvBankPerson {
  readonly candidateId: string;
  readonly name: string;
}

/**
 * Who meets the CV Bank (§L23 Candidate, ADR 0046): HR Lead and Recruiter, never a panelist's
 * interviewee-only reach — and nobody while the module is off (#13).
 */
export function useCvBankAccess(): { readonly canSee: boolean; readonly canEdit: boolean } {
  const isOn = useModule('cv_bank');
  const session = useCurrentSession();
  const mayEdit = useCanEdit('candidate');
  const level = session?.permissions.candidate?.level;
  const canSee = isOn && (level === 'full' || level === 'read');
  return { canSee, canEdit: canSee && mayEdit };
}

/** Opens the bank on its duplicate pairs, where each is settled (Q4). */
function useChooseDuplicates(): () => void {
  const navigate = useNavigate();
  return useCallback(() => void navigate({ to: '/cv-bank', search: { dupes: true } }), [navigate]);
}

/**
 * The profile's button and every selection bar's "Add to CV Bank" (Q2 a, b). The toast says who
 * went in, with Undo for 10 s; a duplicate it made is asked about at once, from its own toast.
 */
export function useAddToCvBank(): { readonly add: (people: readonly CvBankPerson[], source: 'profile' | 'bulk') => Promise<boolean>; readonly isPending: boolean } {
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const chooseDuplicates = useChooseDuplicates();
  const [isPending, setIsPending] = useState(false);

  const add = useCallback(
    async (people: readonly CvBankPerson[], source: 'profile' | 'bulk'): Promise<boolean> => {
      if (people.length === 0) return false;
      setIsPending(true);
      try {
        const result = await cvBankApi.add({ candidateIds: people.map((person) => person.candidateId), source });
        await refreshCvBank(queryClient);
        const { message, detail } = addedMessage(result, people.map((person) => person.name));
        if (result.addedEntryIds.length === 0) {
          show({ tone: 'warning', message, ...(detail ? { detail } : {}) });
          return false;
        }
        showUndo(message, () => {
          void cvBankApi
            .remove({ entryIds: [...result.addedEntryIds], reason: UNDO_ADD_REASON })
            .then(() => refreshCvBank(queryClient))
            .catch((error: unknown) => show({ tone: 'danger', message: "Couldn't undo that", detail: errorMessage(error) }));
        });
        if (detail) show({ tone: 'warning', message: 'Some were not added', detail });
        if (result.duplicates.length > 0) {
          show({ tone: 'info', message: duplicatesMessage(result.duplicates), detail: 'Choose which CV to keep, or keep both.', action: { label: 'Choose', run: chooseDuplicates } });
        }
        return true;
      } catch (error) {
        show({ tone: 'danger', message: 'Nobody was added to the CV Bank', detail: errorMessage(error) });
        return false;
      } finally {
        setIsPending(false);
      }
    },
    [queryClient, show, showUndo, chooseDuplicates],
  );
  return { add, isPending };
}

/** What "Remove from CV Bank" acts on: ticked rows, or everything the search matches (owner Q2 a, Group 8). */
export type CvBankRemoval =
  | { readonly kind: 'ids'; readonly entries: readonly { readonly id: string; readonly name: string }[] }
  | {
      readonly kind: 'all-matching';
      readonly query: string | undefined;
      readonly excludedIds: readonly string[];
      /** What the bar and the popover said, so the toast says the same. */
      readonly count: number;
      /** The rows on screen that go, so they leave at once (§5a rule 1). */
      readonly visibleIds: readonly string[];
    };

/** The toast after a removal: one name, or how many. */
export function removedMessage(removal: CvBankRemoval): string {
  if (removal.kind === 'all-matching') return `${removal.count} removed from the CV Bank`;
  const [only] = removal.entries;
  return removal.entries.length === 1 && only ? `${only.name} removed from the CV Bank` : `${removal.entries.length} removed from the CV Bank`;
}

/**
 * Out of the bank at once (§5a rule 1), back if the server says no, and Undo for 10 s. The toast
 * shows as soon as the server agrees; the lists refresh behind it (§5a: a click never waits).
 */
export function useRemoveFromCvBank(): (removal: CvBankRemoval, reason: string) => Promise<boolean> {
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  return useCallback(
    async (removal, reason) => {
      const leaving = removal.kind === 'ids' ? removal.entries.map((entry) => entry.id) : removal.visibleIds;
      const restore = dropFromLists(queryClient, new Set(leaving));
      try {
        const { removedEntryIds } =
          removal.kind === 'ids'
            ? await cvBankApi.remove({ entryIds: [...leaving], reason })
            : await cvBankApi.removeMatching({ matching: removal.query ? { query: removal.query } : {}, excludedEntryIds: [...removal.excludedIds], reason });
        showUndo(removedMessage(removal), () => {
          void cvBankApi
            .restore(removedEntryIds)
            .then(() => refreshCvBank(queryClient))
            .catch((error: unknown) => show({ tone: 'danger', message: "Couldn't put them back", detail: errorMessage(error) }));
        });
        void refreshCvBank(queryClient);
        return true;
      } catch (error) {
        restore();
        show({ tone: 'danger', message: 'Nothing was removed', detail: errorMessage(error) });
        return false;
      }
    },
    [queryClient, show, showUndo],
  );
}

/** Q4: the pair leaves the list at once; a CV that left the bank can be put back for 10 s. */
export function useResolveDuplicate(): (pair: CvBankDuplicatePair, keep: CvBankDuplicateChoice) => Promise<void> {
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  return useCallback(
    async (pair, keep) => {
      const restorePair = dropPair(queryClient, pair);
      const leaving = keep === 'first' ? pair.second : pair.first;
      const restoreRow = keep === 'both' ? () => undefined : dropFromLists(queryClient, new Set([leaving.id]));
      try {
        const { removedEntryIds } = await cvBankApi.resolve({ firstEntryId: pair.first.id, secondEntryId: pair.second.id, keep });
        void refreshCvBank(queryClient);
        if (keep === 'both') {
          show({ tone: 'success', message: 'Both kept', detail: 'This pair will not be flagged again.' });
          return;
        }
        showUndo(`${leaving.cv.fileName} left the CV Bank`, () => {
          void cvBankApi
            .restore(removedEntryIds)
            .then(() => refreshCvBank(queryClient))
            .catch((error: unknown) => show({ tone: 'danger', message: "Couldn't put it back", detail: errorMessage(error) }));
        });
      } catch (error) {
        restorePair();
        restoreRow();
        show({ tone: 'danger', message: "That wasn't saved", detail: errorMessage(error) });
      }
    },
    [queryClient, show, showUndo],
  );
}
